<template>
    <!-- Not using regular accordion to preserve native radio buttons behaviour: form validation, inability of deselection -->
    <ButtonGroup
        :buttons="actualPaymentMethods"
        :name="$t('Generic.payment')"
        :value.sync="selected.name"
        id="paymentMethodsSelect"
        class="payment-methods-select"
        required
    >
        <template v-slot:Prepayment="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    <p class="xmas-notice" v-if="showXmasPaymentInfo1">
                        {{ $t('Checkout.paymentNotice.prepayment_xmas') }}
                    </p>
                    <p class="xmas-notice" v-else-if="showXmasPaymentInfo2">
                        {{ $t('Checkout.paymentNotice.prepayment_xmas_2') }}
                    </p>
                    {{ $t('Checkout.payment.prepayment') }}
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:AmazonPay="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <!--
                Cannot use v-show here to prevent concurrent amazon pay script injections and button reinitializations.
                Amazon script breaks other logic (e.g. credit card), for some reasons. v-if is required
                -->
                <div v-if="selected.name === button.item.value" class="payment-body">
                    <AmazonPay :amazonOrderReferenceId.sync="selected.amazonOrderReferenceId" ref="amazonPay" />
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:PayPal="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon" />
                <!--                <IconSvg filepath="paymentMethods/visa.svg" />-->
                <!--                <IconSvg filepath="paymentMethods/mastercard.svg" />-->
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    {{ $t('Checkout.payment.payPal', { buttonText: ctaText }) }}

                    <PayPalModal v-if="showPayPalModal" :order="orderResponse" />
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:PayPalPlus="button">
            <span class="payment-header">
                <span class="method-name">{{ $t('PaymentMethod.payPal') }} / {{ $t('PaymentMethod.creditCard') }}</span>
                <IconSvg filepath="paymentMethods/payPal.svg" class="payment-icon" />
                <!--                <IconSvg filepath="paymentMethods/visa.svg" />-->
                <!--                <IconSvg filepath="paymentMethods/mastercard.svg" />-->
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    <translate path="Checkout.payment.payPalPlus">
                        <template v-slot:note
                            ><br /><b style="font-weight: 700">{{
                                $t('Checkout.payment.payPalPlus.note')
                            }}</b></template
                        >
                        <template v-slot:proceed>{{ $t('Checkout.submit.buyNow') }}</template>
                    </translate>

                    <div v-if="ppplusLoadError" class="text-danger">
                        {{ $t('Checkout.payment.payPalPlus.loadError') }}
                    </div>

                    <Modal v-if="showPayPalPlusWall" :width="320" hide-footer @opened="$emit('ppplusOpened', $event)">
                        <h4 slot="header">{{ $t('Checkout.section.paymentData') }}</h4>
                        <div id="ppplus"></div>
                    </Modal>
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:SofortUeberweisung="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    <p class="xmas-notice" v-if="showXmasPaymentInfo1">
                        {{ $t('Checkout.paymentNotice.sofort_xmas') }}
                    </p>
                    <p class="xmas-notice" v-else-if="showXmasPaymentInfo2">
                        {{ $t('Checkout.paymentNotice.prepayment_xmas_2') }}
                    </p>
                    {{ $t('Checkout.payment.sofort', { buttonText: ctaText }) }}
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:Stripe="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg filepath="paymentMethods/visa.svg" class="payment-icon" />
                <IconSvg filepath="paymentMethods/mastercard.svg" class="payment-icon" />
                <IconSvg filepath="paymentMethods/ideal.svg" class="payment-icon ml-1" title="ideal" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    {{ $t('Checkout.payment.stripe', { buttonText: ctaText }) }}
                </div>
            </TransitionExpand>
        </template>

        <template v-slot:Paydirekt="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon icon-paydirekt" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    {{ $t('Checkout.payment.payDirect', { buttonText: ctaText }) }}
                </div>
            </TransitionExpand>
        </template>

        <template v-if="canUseApplePay" v-slot:ApplePay="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon icon-applepay" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    {{ $t('Checkout.payment.applePay', { buttonText: ctaText }) }}
                </div>
            </TransitionExpand>
        </template>

        <template v-if="canUseGooglePay" v-slot:GooglePay="button">
            <span class="payment-header">
                <span class="method-name">{{ $t(button.item.text) }}</span>
                <IconSvg :filepath="button.item.iconSrc" class="payment-icon icon-googlepay" />
                <IconSvg filepath="arrow-down.svg" :class="{ active: selected.name === button.item.value }" />
            </span>
            <TransitionExpand>
                <div v-if="selected.name === button.item.value" class="payment-body">
                    {{ $t('Checkout.payment.googlePay', { buttonText: ctaText }) }}
                </div>
            </TransitionExpand>
        </template>
    </ButtonGroup>
</template>

<script>
import ButtonGroup from '@/components/ButtonGroup';
import TransitionExpand from '@/components/utility/TransitionExpand';
import Modal from '@/components/Modal';
import availablePaymentMethods from '@/models/paymentMethods';
import { appendScript } from '@/utils/injectRemote';
import AmazonPay from '@/components/checkout/AmazonPay';
import PayPalModal from '@/components/checkout/PayPalModal';

export default {
    name: 'PaymentMethodsSelect',
    data() {
        return {
            availablePaymentMethods,
            // creditcard specific properties
            showPayPalPlusWall: false,
            ppplusLoadError: false,
            isProd: process.env.NODE_ENV === 'production',
            showPayPalModal: false,
            orderResponse: null,
            canUseApplePay: false,
            canUseGooglePay: false
        };
    },
    components: {
        PayPalModal,
        ButtonGroup,
        TransitionExpand,
        Modal,
        AmazonPay
    },
    props: {
        paymentMethods: {
            type: Array
        },
        ctaText: String,
        postShippingSelected: Boolean,
        selected: Object
    },
    computed: {
        actualPaymentMethods() {
            //Converting provided payment methods to lowercase, to prevent possible camelCase mismatch
            const paymentMethodsLowercaseUnique = [...new Set(this.paymentMethods.map(method => method.toLowerCase()))];

            //Getting provided paymentMethods among available methods
            const actualPaymentMethods = Object.keys(this.availablePaymentMethods).reduce((final, current) => {
                const method = this.availablePaymentMethods[current];
                if (~paymentMethodsLowercaseUnique.indexOf(method.value.toLowerCase())) {
                    final[method.value.toLowerCase()] = method;
                }
                return final;
            }, {});

            // Make sure to preserve the sort order of the payment methods send from the backend
            return paymentMethodsLowercaseUnique.map(method => actualPaymentMethods[method]);
        },
        showXmasPaymentInfo1() {
            let today = new Date();
            return (
                this.postShippingSelected && today.getMonth() === 11 && today.getDate() >= 15 && today.getDate() < 18
            );
        },
        showXmasPaymentInfo2() {
            let today = new Date();
            return today.getMonth() === 11 && today.getDate() >= 18 && today.getDate() <= 26;
        }
    },
    methods: {
        validateIBAN() {
            //const validIban = paymill.validateIban(this.selected.iban);
            //this.$refs.iban.setCustomValidity(validIban ? "" : this.$t('Paymill.error.invalidIBAN'));
        },
        validateBIC() {
            //const validBic = paymill.validateIban(this.selected.bic);
            //this.$refs.bic.setCustomValidity(validBic ? "" : this.$t('Paymill.error.invalidBIC'));
        },
        initAmazonConfirmationFlow() {
            return this.$refs.amazonPay.initConfirmationFlow();
        },
        async addPayPalPlusScript() {
            if (process.client) {
                this.ppplusLoadError = false;
                try {
                    appendScript('https://www.paypalobjects.com/webstatic/ppplus/ppplus.min.js');
                } catch (e) {
                    this.ppplusLoadError = true;
                }
            }
        },

        processPayPalPlus(response) {
            this.showPayPalPlusWall = true;
            this.$once('ppplusOpened', () => {
                const ppp = PAYPAL.apps.PPP({
                    approvalUrl: response.next ? response.next : response.redirectUrl,
                    placeholder: 'ppplus',
                    preselection: 'none',
                    mode: this.isProd ? 'live' : 'sandbox',
                    country: 'DE',
                    language: this.$store.state.i18n.locale,
                    buttonLocation: 'inside'
                });
            });
        },
        processPayPal(orderResult) {
            this.orderResponse = orderResult;
            this.showPayPalModal = true;
        },

        updateSelectedProp(prop, value) {
            this.$emit('update:selected', {
                ...this.selected,
                [prop]: value
            });
        },
        hasApplePaySupport() {
            return process.client && !!window.ApplePaySession;
        },
        async hasGooglePaySupport() {
            if (process.client && window.PaymentRequest) {
                try {
                    const request = new PaymentRequest([{ supportedMethods: 'https://google.com/pay' }], {
                        //id: 123 //id can be omitted, not available in current scope
                        total: { label: 'Total', amount: { currency: 'EUR', value: '0' } }
                    });

                    return await request.canMakePayment();
                } catch (e) {
                    return Promise.resolve(false);
                }
            }
            return Promise.resolve(false);
        }
    },
    async created() {
        if (this.paymentMethods.includes(availablePaymentMethods.applePay.value)) {
            this.canUseApplePay = this.hasApplePaySupport();
        }

        if (this.paymentMethods.includes(availablePaymentMethods.googlePay.value)) {
            this.canUseGooglePay = await this.hasGooglePaySupport();
        }
    },
    watch: {
        'selected.iban'(iban) {
            if (iban) {
                this.validateIBAN();
            }
        },
        'selected.bic'(bic) {
            if (bic) {
                this.validateBIC();
            }
        },
        selected: {
            handler(selected) {
                if (selected && selected.name === availablePaymentMethods.payPalPlus.value) {
                    this.addPayPalPlusScript();
                }
                this.$emit('update:selected', selected);
            },
            immediate: true,
            deep: true //on checkout for order the watcher will get preselected Object and upon change it will remain object => no watcher fired
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/theming' as *;

#paymentMethodsSelect {
    display: flex;
    flex-direction: column;

    .payment-header svg.icon-arrow-down {
        color: var(--inputs-empty-color);
    }

    ::v-deep .btn.active .payment-body {
        border-top: 1px solid var(--border-color);
    }

    .payment-header {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;

        .method-name {
            text-align: left;
            white-space: nowrap;
            margin-right: 10px;
        }

        svg.payment-icon:first-of-type,
        img.payment-icon:first-of-type {
            margin-left: auto;
        }

        svg.icon-arrow-down {
            font-size: 24px;
            margin-left: 10px;

            &.active {
                transform: rotate(180deg);
            }
        }

        .icon-vorkasse {
            height: 28px;
        }

        .icon-ideal {
            font-size: 2em;
        }
    }

    .payment-body {
        text-align: left;
        line-height: 20px;

        @include variables.ie {
            visibility: visible !important;
            position: static !important;
        }

        .xmas-notice {
            border: 2px solid hsla(19, 100%, 59%, 0.5);
            padding: 5px 15px;
            margin-bottom: 10px;
            background: hsl(0, 0%, 95%);
        }
    }

    ::v-deep .btn.active .payment-body {
        margin-top: 10px;
        padding-top: 10px;
    }

    .icon-paydirekt {
        font-size: 28px;
    }
}
</style>
